import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import irelandApi from '../irelandApi';
import { REP_USER_GROUPS } from '../data/userGroups';

function tokenValid(token) {
  try {
    return jwtDecode(token).exp > Math.round(new Date().getTime() / 1000);
  } catch (error) {
    toast.error('Invalid JWT token provided');
    return false;
  }
}

export function clear(tokenName = 'accessToken') {
  localStorage.removeItem(tokenName);
  delete irelandApi.defaults.headers.common.Authorization;
  delete axios.defaults.headers.common.Authorization;
}

export function setAuthorizationToken(token, name = 'accessToken') {
  if (token) {
    localStorage.setItem(name, token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    // axios 1.x snapshots the default headers into each instance at create time, so a
    // token set on the global default AFTER `axios.create()` never reaches the already
    // created `irelandApi` instance — which every API call goes through. Without this
    // line a fresh admin login leaves `irelandApi` unauthenticated, so the immediate
    // `/api/admin/users/my_profile` request 401s and the user is bounced back to
    // /admin/login with "You do not have access to admin area". Mirrors clear() below.
    irelandApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    clear();
  }
}

// Declared as a hoisted function, not a const arrow: tokenUtils and irelandApi
// import each other, and irelandApi calls this at module scope. If tokenUtils is
// the module that enters the cycle, a const arrow is still in its TDZ by then.
export function getAuthorizationToken() {
  return localStorage.getItem('accessToken');
}

export function check(token) {
  const theToken = token || getAuthorizationToken();
  if (!theToken) return false;
  return theToken && tokenValid(theToken);
}

export function isLoggedIn() {
  const token = getAuthorizationToken();
  return token && check(token) && jwtDecode(token).scp !== 'pending_user';
}

function storedTokenClaims() {
  const token = getAuthorizationToken();
  if (!token) return {};
  try {
    return jwtDecode(token) || {};
  } catch (error) {
    return {};
  }
}

function sessionClaims() {
  const payload = storedTokenClaims();
  if (!payload.exp || payload.exp <= Math.round(new Date().getTime() / 1000)) return {};
  return payload;
}

export function hasSessionGroupClaims() {
  const token = getAuthorizationToken();
  if (!token) return true;
  try {
    return Object.prototype.hasOwnProperty.call(jwtDecode(token), 'user_group');
  } catch (error) {
    return false;
  }
}

export function isPseudoLoggedIn() {
  return sessionClaims().impersonation === true;
}

export function currentUserGroup() {
  return sessionClaims().user_group;
}

export function originalUserGroup() {
  return sessionClaims().original_user_group;
}

export function originalUserIsRepUser() {
  return REP_USER_GROUPS.includes(originalUserGroup());
}

export function storedOriginalUserGroup() {
  return storedTokenClaims().original_user_group;
}

export function storedImpersonationScope() {
  const claims = storedTokenClaims();
  return claims.impersonation ? claims.impersonation_scope : undefined;
}

export function isAuditorLoggedIn() {
  return isPseudoLoggedIn() && currentUserGroup() === 'savi_auditor';
}

export function isRdrPseudoLoggedIn() {
  return isPseudoLoggedIn() && originalUserGroup() === 'rdr';
}

export function isAbmPseudoLoggedIn() {
  return isPseudoLoggedIn() && originalUserGroup() === 'abm';
}

export function isAbmLoggedIn() {
  return currentUserGroup() === 'abm';
}
